// UI translations (EN / IT). Destination content lives in src/data/geo*.json.
export const t = {
  en: {
    nav: {
      carRental: "Car Rental",
      destinations: "Destinations",
      airports: "Airports",
      deals: "Deals",
      help: "Help",
      about: "About",
      contact: "Contact",
      menu: "Menu",
    },
    hero: {
      kicker: "Worldwide car rental comparison",
      line1: "Compare car rentals.",
      line2: "Find the right deal.",
      sub: "Search available offers worldwide and choose the car that fits your trip.",
      points: [
        "Airport & city pick-up",
        "Trusted booking partner",
        "Transparent search",
        "Free cancellation on most bookings",
      ],
    },
    widget: {
      submitText: "Search cars",
      titleText: "Search and compare car rentals and save up to 70%!",
      descText: "We've selected the best deals from our car rental partners.",
    },
    home: {
      destTitle: "Popular destinations",
      destSub: "Pick a starting point — local guides and live search for every city.",
      viewAll: "All destinations",
      airTitle: "Popular airports",
      airSub: "Land, collect the keys, go. The busiest rental pick-up points, compared.",
      whyTitle: "Why BoingCars",
      whySub: "We do one thing: make finding the right rental car simple, honest and fast.",
      why: [
        {
          t: "Worldwide search",
          d: "One search covers airports and city offices across the globe — from Rome to Dubai to Miami.",
        },
        {
          t: "Simple comparison",
          d: "Suppliers, car categories, fuel policies and deposits side by side, so the right choice is obvious.",
        },
        {
          t: "Airport & city locations",
          d: "Pick up where your trip actually starts — the terminal, the station or the neighbourhood office.",
        },
        {
          t: "Mobile friendly",
          d: "Built for the phone in your hand: search, compare and book from anywhere, in minutes.",
        },
        {
          t: "Trusted booking infrastructure",
          d: "Search and booking run on DiscoverCars, an established international rental platform. BoingCars helps you discover and compare.",
        },
      ],
      howTitle: "How it works",
      howSub: "Three steps between you and the open road.",
      steps: [
        { t: "Search", d: "Tell us where and when. The engine scans airport and city locations worldwide." },
        { t: "Compare", d: "See offers side by side — car type, deposit, fuel policy and supplier ratings." },
        { t: "Book", d: "Confirm in a few clicks through our booking partner and collect the keys on arrival." },
      ],
      faqTitle: "Questions, answered",
      faqSub: "The essentials before you book. Conditions vary by supplier — always read the rental terms shown for your chosen car.",
      dealsTitle: "Deals start with a good search",
      dealsText:
        "Rental prices change with season, demand and availability — that is why we do not show static price tables. Run a live search for your dates and destination to see today's real offers.",
      dealsCta: "Search cars",
    },
    common: {
      home: "Home",
      searchCars: "Search cars",
      compareDeals: "Compare deals",
      affiliateNote:
        "Car rental search and booking are provided by our partner DiscoverCars. BoingCars may receive a commission from qualifying bookings — at no extra cost to you.",
      relatedTitle: "Keep exploring",
      faqTitle: "Frequently asked questions",
      pickupTitle: "Pick-up locations",
      drivingTitle: "Driving there",
      localTitle: "Good to know",
      readGuide: "Read the guide",
      breadcrumbDestinations: "Destinations",
      breadcrumbAirports: "Airports",
    },
    help: {
      title: "Help & FAQ",
      sub: "Straight answers about renting a car — what you need, what it costs to watch for, and how pick-up works. Conditions always depend on the rental supplier, so treat these as orientation and confirm in your rental terms.",
      faqs: [
        {
          q: "What do I need to rent a car?",
          a: "Typically: a driving licence held for a minimum period (often 1–2 years), a passport or ID, and a payment card in the main driver's name. Exact requirements are set by each supplier and shown during booking.",
        },
        {
          q: "How old do I have to be to rent a car?",
          a: "It varies by country and supplier. Many set the minimum between 21 and 25, and drivers under 25 often pay a young-driver surcharge. The age rules for your specific car are listed in its rental conditions.",
        },
        {
          q: "Can I rent with a debit card?",
          a: "Some suppliers accept debit cards, many require a credit card in the main driver's name for the security deposit. Card requirements are stated in the rental terms for each offer — check before booking.",
        },
        {
          q: "How does the security deposit work?",
          a: "At pick-up, the supplier blocks a deposit on the main driver's card; it is released after return, though banks can take days to unblock it. The deposit amount for each car is shown during booking.",
        },
        {
          q: "Is insurance included?",
          a: "Rentals normally include basic cover with an excess (the part you pay in case of damage). You can usually reduce the excess with optional cover at booking or at the desk. What is included is itemised in each offer.",
        },
        {
          q: "Can I cancel my booking?",
          a: "Many bookings offer free cancellation up to a deadline before pick-up, but the exact policy depends on the rate you choose. The cancellation terms are displayed before you confirm.",
        },
        {
          q: "How does airport pick-up work?",
          a: "Your voucher states where to go: an in-terminal desk, a rental centre reached by shuttle or train, or a meet-and-greet. Allow time for the transfer at large airports, and check desk hours if your flight lands late.",
        },
        {
          q: "Can I add an additional driver?",
          a: "Yes, most suppliers allow it for a daily fee, and the extra driver must show their licence at pick-up. Some rates include a free additional driver — it is listed in the offer details.",
        },
        {
          q: "Can I take the car across a border?",
          a: "Often yes, but it always requires the supplier's permission and sometimes a fee or extra cover. Rules differ per supplier and country pair — confirm cross-border terms before booking, not at the border.",
        },
      ],
    },
    contact: {
      title: "Contact us",
      sub: "Questions about the site, partnerships or press — write to us. For questions about an existing booking, please contact the rental supplier shown on your voucher, or the DiscoverCars support team.",
      name: "Name",
      email: "Email",
      subject: "Topic",
      subjects: { general: "General question", booking: "Booking help", affiliate: "Affiliate / partnership", press: "Press" },
      message: "Message",
      send: "Send message",
      sending: "Sending…",
      success: "Message sent — we will reply by email shortly.",
      error: "Something went wrong. Please try again or email us directly.",
      direct: "Prefer email?",
    },
    deals: {
      title: "Car rental deals",
      sub: "We do not publish static price tables — rental prices move with season, demand and availability, and a stale price is worse than no price. The honest deal is a live comparison for your dates.",
      popular: "Popular places to search right now",
    },
    cookie: {
      title: "Your privacy, your call",
      text: "We use essential cookies to run the site and, with your consent, analytics cookies to understand how it is used. No marketing trackers load without your yes.",
      accept: "Accept analytics",
      reject: "Essentials only",
      policy: "Cookie policy",
    },
    footer: {
      tagline: "Rent smarter. Drive anywhere.",
      desc: "BoingCars is an independent car rental discovery website. Search and booking are provided by our partner DiscoverCars; we may earn a commission on qualifying bookings.",
      explore: "Explore",
      company: "Company",
      legal: "Legal",
      rights: "All rights reserved.",
      disclosure: "Affiliate disclosure",
    },
    notFound: {
      title: "This road goes nowhere.",
      sub: "The page you are looking for does not exist or has moved.",
      cta: "Back to the search",
    },
  },
  it: {
    nav: {
      carRental: "Noleggio auto",
      destinations: "Destinazioni",
      airports: "Aeroporti",
      deals: "Offerte",
      help: "Assistenza",
      about: "Chi siamo",
      contact: "Contatti",
      menu: "Menu",
    },
    hero: {
      kicker: "Confronto noleggio auto in tutto il mondo",
      line1: "Confronta le auto a noleggio.",
      line2: "Trova l'offerta giusta.",
      sub: "Cerca tra le offerte disponibili in tutto il mondo e scegli l'auto più adatta al tuo viaggio.",
      points: [
        "Ritiro in aeroporto e in città",
        "Partner di prenotazione affidabile",
        "Ricerca trasparente",
        "Cancellazione gratuita sulla maggior parte delle prenotazioni",
      ],
    },
    widget: {
      submitText: "Cerca auto",
      titleText: "Cerca e confronta noleggi auto e risparmia fino al 70%!",
      descText: "Abbiamo selezionato le migliori offerte dei nostri partner di noleggio.",
    },
    home: {
      destTitle: "Destinazioni popolari",
      destSub: "Scegli il punto di partenza: guide locali e ricerca live per ogni città.",
      viewAll: "Tutte le destinazioni",
      airTitle: "Aeroporti popolari",
      airSub: "Atterra, ritira le chiavi, parti. I principali punti di ritiro, a confronto.",
      whyTitle: "Perché BoingCars",
      whySub: "Facciamo una cosa sola: rendere semplice, onesta e veloce la ricerca dell'auto a noleggio giusta.",
      why: [
        {
          t: "Ricerca mondiale",
          d: "Un'unica ricerca copre aeroporti e uffici in città in tutto il mondo: da Roma a Dubai a Miami.",
        },
        {
          t: "Confronto semplice",
          d: "Fornitori, categorie di auto, politiche carburante e depositi fianco a fianco: la scelta giusta è evidente.",
        },
        {
          t: "Aeroporti e città",
          d: "Ritira dove il viaggio inizia davvero: il terminal, la stazione o l'ufficio di quartiere.",
        },
        {
          t: "Pensato per il mobile",
          d: "Costruito per il telefono che hai in mano: cerca, confronta e prenota da ovunque, in pochi minuti.",
        },
        {
          t: "Infrastruttura affidabile",
          d: "Ricerca e prenotazione funzionano su DiscoverCars, una piattaforma internazionale consolidata. BoingCars ti aiuta a scoprire e confrontare.",
        },
      ],
      howTitle: "Come funziona",
      howSub: "Tre passi tra te e la strada aperta.",
      steps: [
        { t: "Cerca", d: "Dicci dove e quando. Il motore scansiona sedi in aeroporto e in città in tutto il mondo." },
        { t: "Confronta", d: "Vedi le offerte fianco a fianco: tipo di auto, deposito, politica carburante e valutazioni dei fornitori." },
        { t: "Prenota", d: "Conferma in pochi clic tramite il nostro partner di prenotazione e ritira le chiavi all'arrivo." },
      ],
      faqTitle: "Domande e risposte",
      faqSub: "L'essenziale prima di prenotare. Le condizioni variano a seconda del fornitore: leggi sempre i termini di noleggio dell'auto scelta.",
      dealsTitle: "Le offerte iniziano da una buona ricerca",
      dealsText:
        "I prezzi del noleggio cambiano con stagione, domanda e disponibilità: per questo non mostriamo tabelle di prezzi statiche. Fai una ricerca live per le tue date e la tua destinazione per vedere le offerte reali di oggi.",
      dealsCta: "Cerca auto",
    },
    common: {
      home: "Home",
      searchCars: "Cerca auto",
      compareDeals: "Confronta le offerte",
      affiliateNote:
        "La ricerca e la prenotazione del noleggio auto sono fornite dal nostro partner DiscoverCars. BoingCars può ricevere una commissione dalle prenotazioni idonee — senza costi aggiuntivi per te.",
      relatedTitle: "Continua a esplorare",
      faqTitle: "Domande frequenti",
      pickupTitle: "Punti di ritiro",
      drivingTitle: "Guidare sul posto",
      localTitle: "Buono a sapersi",
      readGuide: "Leggi la guida",
      breadcrumbDestinations: "Destinazioni",
      breadcrumbAirports: "Aeroporti",
    },
    help: {
      title: "Assistenza e FAQ",
      sub: "Risposte chiare sul noleggio auto: cosa serve, quali costi tenere d'occhio e come funziona il ritiro. Le condizioni dipendono sempre dal fornitore: usa queste risposte come orientamento e conferma nei termini di noleggio.",
      faqs: [
        {
          q: "Cosa serve per noleggiare un'auto?",
          a: "In genere: una patente di guida posseduta da un periodo minimo (spesso 1–2 anni), un passaporto o documento d'identità e una carta di pagamento intestata al guidatore principale. I requisiti esatti sono fissati da ciascun fornitore e mostrati in prenotazione.",
        },
        {
          q: "Che età bisogna avere per noleggiare un'auto?",
          a: "Varia in base al paese e al fornitore. Molti fissano il minimo tra 21 e 25 anni, e i guidatori sotto i 25 spesso pagano un supplemento giovane guidatore. Le regole sull'età per l'auto specifica sono indicate nelle condizioni di noleggio.",
        },
        {
          q: "Posso noleggiare con una carta di debito?",
          a: "Alcuni fornitori accettano carte di debito, molti richiedono una carta di credito intestata al guidatore principale per il deposito cauzionale. I requisiti sulla carta sono indicati nei termini di ogni offerta: controlla prima di prenotare.",
        },
        {
          q: "Come funziona il deposito cauzionale?",
          a: "Al ritiro, il fornitore blocca un deposito sulla carta del guidatore principale; viene svincolato dopo la riconsegna, anche se le banche possono impiegare giorni. L'importo del deposito per ogni auto è mostrato in prenotazione.",
        },
        {
          q: "L'assicurazione è inclusa?",
          a: "I noleggi includono normalmente una copertura base con franchigia (la parte che paghi in caso di danno). Di solito puoi ridurre la franchigia con una copertura opzionale in prenotazione o al banco. Cosa è incluso è dettagliato in ogni offerta.",
        },
        {
          q: "Posso cancellare la prenotazione?",
          a: "Molte prenotazioni offrono la cancellazione gratuita fino a una scadenza prima del ritiro, ma la politica esatta dipende dalla tariffa scelta. Le condizioni di cancellazione sono mostrate prima della conferma.",
        },
        {
          q: "Come funziona il ritiro in aeroporto?",
          a: "Il voucher indica dove andare: un banco nel terminal, un centro noleggi raggiungibile con navetta o treno, oppure un servizio di incontro. Prevedi tempo per il trasferimento nei grandi aeroporti e controlla gli orari del banco se il volo atterra tardi.",
        },
        {
          q: "Posso aggiungere un secondo guidatore?",
          a: "Sì, la maggior parte dei fornitori lo permette con un costo giornaliero, e il guidatore aggiuntivo deve mostrare la patente al ritiro. Alcune tariffe includono un secondo guidatore gratuito: è indicato nei dettagli dell'offerta.",
        },
        {
          q: "Posso portare l'auto oltre confine?",
          a: "Spesso sì, ma serve sempre l'autorizzazione del fornitore e talvolta un costo o una copertura extra. Le regole variano per fornitore e coppia di paesi: conferma le condizioni transfrontaliere prima di prenotare, non al confine.",
        },
      ],
    },
    contact: {
      title: "Contattaci",
      sub: "Domande sul sito, partnership o stampa: scrivici. Per domande su una prenotazione esistente, contatta il fornitore indicato sul voucher o il supporto di DiscoverCars.",
      name: "Nome",
      email: "Email",
      subject: "Argomento",
      subjects: { general: "Domanda generale", booking: "Aiuto prenotazione", affiliate: "Affiliazione / partnership", press: "Stampa" },
      message: "Messaggio",
      send: "Invia messaggio",
      sending: "Invio in corso…",
      success: "Messaggio inviato — ti risponderemo via email a breve.",
      error: "Qualcosa è andato storto. Riprova o scrivici direttamente via email.",
      direct: "Preferisci l'email?",
    },
    deals: {
      title: "Offerte noleggio auto",
      sub: "Non pubblichiamo tabelle di prezzi statiche: i prezzi del noleggio si muovono con stagione, domanda e disponibilità, e un prezzo vecchio è peggio di nessun prezzo. L'offerta onesta è un confronto live per le tue date.",
      popular: "Le mete più cercate in questo momento",
    },
    cookie: {
      title: "La tua privacy, la tua scelta",
      text: "Usiamo cookie essenziali per far funzionare il sito e, con il tuo consenso, cookie analitici per capire come viene usato. Nessun tracker di marketing si carica senza il tuo sì.",
      accept: "Accetta analitici",
      reject: "Solo essenziali",
      policy: "Cookie policy",
    },
    footer: {
      tagline: "Noleggia meglio. Guida ovunque.",
      desc: "BoingCars è un sito indipendente di scoperta del noleggio auto. Ricerca e prenotazione sono fornite dal nostro partner DiscoverCars; possiamo guadagnare una commissione sulle prenotazioni idonee.",
      explore: "Esplora",
      company: "Azienda",
      legal: "Legale",
      rights: "Tutti i diritti riservati.",
      disclosure: "Divulgazione affiliazione",
    },
    notFound: {
      title: "Questa strada non porta da nessuna parte.",
      sub: "La pagina che cerchi non esiste o è stata spostata.",
      cta: "Torna alla ricerca",
    },
  },
};
